import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';

import { parsePrice, formatPrice } from '../../../utils/utils';
import Icon from '../../Icons';
import { cardMapping } from '../constants';
import { showLoader } from '../../Loader/action';
import { updateTransaction } from '../promiseAction';
import { processTransaction } from '../promiseAction';
import { redirectToBankPage } from '../processTransactionResponse';
import { addMoney } from '../promiseAction';
import { setMoney } from '../action';

import { SavedCard } from './styles';
import { sendEvent2 } from '../../../utils/tracking';

const cardTypeMapping: any = {
  VISA: 'visa',
  RUPAY: 'rupay',
  MASTER: 'mastercard',
};

const Form: FunctionComponent<{
  changeHandler: Function;
  activeCard: any;
  name: string;
  data: any;
  amount: number;
  type: string;
  updateInternetCharges: Function;
  paymentInfo: any;
  internetCharges: number;
  fromFastag: any;
}> = ({
  changeHandler,
  activeCard,
  name,
  data,
  amount,
  type,
  updateInternetCharges,
  paymentInfo,
  internetCharges,
  fromFastag,
}) => {
  const { register, handleSubmit, reset, errors } = useForm();
  const dispatch = useDispatch();
  const fAmount = formatPrice(amount);

  const [error, setError] = useState('');
  const [active, setActive] = useState(false);

  const dispatchLoader = useCallback((payload: any) => dispatch(showLoader(payload)), [dispatch]);

  const dispatchInitiatePayment = useCallback(
    (payload: any) =>
      dispatch(addMoney({ amount })).then((resp: any) => {
        if (resp.data.success) {
          dispatch(setMoney(resp.data));
          dispatchLoader({ show: false });
        } else {
          payload.retry++;
          if (payload.retry < 4) {
            dispatchInitiatePayment({
              amount,
              retry: payload.retry,
            });
          } else {
            dispatchLoader({ show: false });
            setError('Something went wrong. Please try again.');
          }
        }
      }),
    // TODO: handle dependencies amount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, dispatchLoader],
  );

  const dispatchProcessTransaction = useCallback(
    (payload: any) =>
      dispatch(processTransaction(payload.data)).then((resp: any) => {
        const resultInfo = _get(resp, 'data.body.resultInfo', {});
        const txnInfo = _get(resp, 'data.body.txnInfo', {});
        if (resultInfo.resultStatus === 'S') {
          redirectToBankPage(payload.data.paymentMode, resp);
        } else {
          if (txnInfo && txnInfo.RESPMSG) {
            setError(txnInfo.RESPMSG);
          } else {
            setError('Something went wrong');
          }
          dispatchInitiatePayment({ amount: payload.amount, retry: 1 });
        }
      }),
    [dispatch, dispatchInitiatePayment],
  );

  const dispatchUpdateTransaction = useCallback(
    (payload: any) =>
      dispatch(updateTransaction(payload.params)).then((resp: any) => {
        if (resp.data.success) {
          const data: any = {
            requestType: 'NATIVE',
            mId: paymentInfo.merchantId,
            orderId: paymentInfo.transactionCode,
            token: paymentInfo.checkSumHash,
            ...payload.cardInfo,
          };
          dispatchProcessTransaction({ data, amount: payload.params.amount });
        } else {
          dispatchLoader({ show: false });
          setError(_get(resp, 'data.message', 'Something went wrong'));
        }
      }),
    [dispatch, paymentInfo, dispatchLoader, dispatchProcessTransaction],
  );

  const onSubmit = (values?: any) => {
    const cvv = values[`${name}_cvv`];
    dispatchLoader({ show: true });
    const requestParam: any = {
      params: {
        appVersion: 'web',
        paymentMode: cardMapping[data.cardDetails.cardType],
        serviceProvider: 'PAYTM CUSTOM',
        userCode: paymentInfo.customerId,
        transactionCode: paymentInfo.transactionCode,
        transactionToken: paymentInfo.checkSumHash,
        amount: fAmount,
        pspAmount: parseFloat(formatPrice(internetCharges || 0)),
      },
      cardInfo: {
        cardInfo: `${data.cardDetails.cardId}||${cvv}|`,
        paymentMode: data.cardDetails.cardType,
      },
    };
    if (fromFastag?.fromFastag === true) {
      sendEvent2(
        'pay_btn',
        {
          event_action: 'click',
          screen_name: 'wallet_recharge_screen',
          miscellaneous: JSON.stringify({
            type: 'saved_cards',
          }),
          target_product: 'fastag',
          event_platform: 'web',
        },
        true,
      );
    }
    dispatchUpdateTransaction(requestParam);
  };

  useEffect(() => {
    if (activeCard.name !== name) {
      reset();
      setActive(false);
      setError('');
    }
    // TODO: handle dependencies name
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard, reset]);

  return (
    <div className="p16 mode-container">
      <form className="form-container pr8" onSubmit={handleSubmit(onSubmit)}>
        <SavedCard className="flex justify-content-s card-info">
          <div className="info-column card-info_number">
            <p className="small mb8 font-light-grey form-label">{data.displayName}</p>
            <div className="flex">
              <p className="card-num mr30">**** **** **** {data.cardDetails.lastFourDigit}</p>
              <Icon name={cardTypeMapping[data.channelCode]} path="payment" />
            </div>
          </div>
          <div className={`info-column ${errors[`${name}_cvv`] ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">CVV</p>
            <input
              type="password"
              placeholder="123"
              className="form-input"
              name={`${name}_cvv`}
              onChange={(e) => {
                setError('');
                changeHandler(name, e.target.value);
              }}
              onFocus={() => {
                setActive(true);
                changeHandler(name);
                updateInternetCharges({ [type]: cardMapping[data.cardDetails.cardType] });
              }}
              onBlur={(e) => {
                if (e.target.value) {
                  return;
                }
                setActive(false);
                updateInternetCharges({ [type]: '' });
              }}
              ref={register({
                maxLength: {
                  value: data.cardDetails.cvvLength,
                  message: `Max. length should be ${data.cardDetails.cvvLength}`,
                },
                minLength: {
                  value: data.cardDetails.cvvLength,
                  message: `Min. length should be ${data.cardDetails.cvvLength}`,
                },
                pattern: {
                  value: /^\d+$/,
                  message: 'Only digits are allowed',
                },
              })}
            />
            <p className="form-error absolute">{errors[`${name}_cvv`] && errors[`${name}_cvv`].message}</p>
          </div>
          <div className="info-column text-right">
            <input
              className={`form-btn ${active ? '' : 'vis-hidden'}`}
              type="submit"
              value={`Pay ${parsePrice({ price: amount + internetCharges })}`}
            />
          </div>
        </SavedCard>
        <p className={`form-error ${error ? '' : 'none'}`}>{error}</p>
      </form>
    </div>
  );
};

export default Form;
